import { Outlet } from 'react-router-dom';
import { ConsoleShell, type ConsoleNavItem } from '@/components';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useAuth } from '@/auth/AuthProvider';
import { useInvites } from '@/hooks/queries';
import { roleDefs } from '@/data/roleDefs';
import { MobileConsoleScaffold } from './MobileConsoleScaffold';

// Full console — Super Admin only. Includes a bridge to the instructor console
// and the org-wide appearance controls.
const SUPER_ITEMS: ConsoleNavItem[] = [
  { section: 'Overview' },
  { to: '/admin', end: true, icon: 'home', label: 'Dashboard' },
  { to: '/admin/reports', icon: 'chart', label: 'Reports' },
  { section: 'People' },
  { to: '/admin/invites', icon: 'flag', label: 'Invites' },
  { to: '/admin/users', icon: 'users', label: 'User management' },
  { to: '/admin/org', icon: 'bldg', label: 'Organization' },
  { section: 'Records' },
  { to: '/admin/courses', icon: 'book', label: 'Courses' },
  { to: '/admin/verify', icon: 'shield', label: 'Verify certificate' },
  { section: 'Teaching' },
  { to: '/teach', icon: 'edit', label: 'Instructor console' },
  { section: 'System' },
  { to: '/admin/appearance', icon: 'sliders', label: 'Appearance' },
];

// Other admins generate invites AND help on the floor (check-in + analytics) via
// the instructor console — admin outranks instructor, so /teach is open to them.
const ADMIN_ITEMS: ConsoleNavItem[] = [
  { section: 'People' },
  { to: '/admin/invites', icon: 'flag', label: 'Invites' },
  { section: 'Teaching' },
  { to: '/teach', icon: 'qr', label: 'Check-in & analytics' },
];

const SUPER_TABS = [
  { to: '/admin', end: true, icon: 'home' as const, label: 'Home' },
  { to: '/admin/invites', icon: 'flag' as const, label: 'Invites' },
  { to: '/admin/users', icon: 'users' as const, label: 'Users' },
  { to: '/admin/verify', icon: 'shield' as const, label: 'Verify' },
  // Bridge back to the learner side — admins are learners too (not a role switch).
  { to: '/learn', icon: 'play' as const, label: 'Learn' },
];

const ADMIN_TABS = [
  { to: '/admin/invites', end: true, icon: 'flag' as const, label: 'Invites' },
  { to: '/teach', icon: 'qr' as const, label: 'Teaching' },
  { to: '/learn', icon: 'play' as const, label: 'Learn' },
];

export function AdminLayout() {
  const isDesktop = useIsDesktop();
  const { user } = useAuth();
  const { data: invites = [] } = useInvites();
  const activeInvites = invites.filter((i) => i.status === 'active').length;
  const isSuper = user?.role === 'superadmin';
  const roleLabel = user ? roleDefs[user.role].label.en : 'Admin';
  const base = isSuper ? SUPER_ITEMS : ADMIN_ITEMS;
  const items = base.map((it) => (it.to === '/admin/invites' ? { ...it, badge: activeInvites || undefined } : it));
  const who = {
    name: user ? `${user.title} ${user.firstEn}` : 'Admin',
    sub: roleLabel,
    initials: user?.initials ?? 'PA',
    tone: user?.tone ?? ('sage' as const),
  };

  if (!isDesktop) {
    return (
      <MobileConsoleScaffold tabs={isSuper ? SUPER_TABS : ADMIN_TABS}>
        <Outlet />
      </MobileConsoleScaffold>
    );
  }

  return (
    <ConsoleShell brand="Warm Paper" roleLabel={user?.role === 'superadmin' ? 'Super Admin' : 'Admin'} items={items} who={who}>
      <Outlet />
    </ConsoleShell>
  );
}
